import React, { useState, useMemo, useEffect, useRef } from "react";
import Pagination from "../../../../components/Pagination";
import { showClass, showGrade, showStudentURL } from "../../../../utils/textUtils";
import StudentMoreButton from "./StudentMoreButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import DeleteStudentButton from "./DeleteStudentsButton";

const StudentTable = ({ students }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 14; // 페이지 당 학생 수

  const [selectedStudents, setSelectedStudents] = useState([]);

  const totalPages = Math.ceil(students.length / itemsPerPage);

  const currentStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return students.slice(startIndex, endIndex);
  }, [students, currentPage, itemsPerPage]);

  // 페이지 번호 목록 만들기 (가령 1~5, 6~10처럼 가변으로)
  const visiblePages = useMemo(() => {
    if (totalPages === 0) return [];
    const startPage = Math.max(2, currentPage - 2); // 2부터 시작 (1은 항상 보여줌)
    const endPage = Math.min(totalPages - 1, currentPage + 2); // 마지막 페이지는 항상 별도 처리

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // 첫 페이지, 마지막 페이지 처리
    const withBoundaries = [1];
    if (startPage > 2) {
      withBoundaries.push("..."); // 첫 페이지와 시작 사이에 ...
    }
    withBoundaries.push(...pages);
    if (endPage < totalPages - 1) {
      withBoundaries.push("..."); // 끝 페이지와 마지막 사이에 ...
    }
    if (totalPages > 1) {
      withBoundaries.push(totalPages); // 마지막 페이지 추가
    }

    return withBoundaries;
  }, [currentPage, totalPages]);

  // 페이지 변경 핸들러
  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  // **체크박스 관련 로직**

  // (1) 개별 학생 체크박스 핸들러
  const handleCheckboxChange = (checked, student) => {
    if (checked) {
      // 체크 O
      setSelectedStudents((prev) => [...prev, student]);
    } else {
      // 체크 해제
      setSelectedStudents((prev) =>
        prev.filter((prevStudent) => prevStudent.id !== student.id)
      );
    }
  };

  // (2) 헤더(전체선택) 체크박스 핸들러
  const handleHeaderCheckboxChange = (checked) => {
    if (checked) {
      setSelectedStudents(currentStudents);
    } else {
      // 해제
      setSelectedStudents([]);
    }
  };

  // (3) 헤더 체크박스가 "완전히 체크" 상태인지 판별 (현재 페이지 모두 체크된 경우)
  const isHeaderChecked =
    currentStudents.length > 0 &&
    currentStudents.every((currentStudent) =>
      selectedStudents.some(
        (selectedStudent) => selectedStudent.id === currentStudent.id
      )
    );

  const overlayStyle = {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100%",
    backgroundColor: "#2D2D2D",
    color: "#fff",
    padding: "20px",
    textAlign: "center",
    zIndex: 9999,
  };

  return (
    <div
      style={{
        width: "90%",
        border: "1px solid #EBEBEB",
        backgroundColor: "#fff",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          padding: "10px",
        }}
      >
        <colgroup>
          <col style={{ width: "7%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "6%" }} />
          <col />
          <col style={{ width: "18%" }} />
          <col style={{ width: "18%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "5%" }} />
        </colgroup>
        <tbody>
          <tr
            style={{
              textAlign: "left",
              borderBottom: "1px solid #EBEBEB",
              fontSize: "14px",
              color: "#737373",
            }}
          >
            <td
              style={{
                textAlign: "center",
              }}
            >
              <CustomCheckbox
                checked={isHeaderChecked}
                onChange={(e) => handleHeaderCheckboxChange(e.target.checked)}
              />
            </td>
            <td style={{ padding: "14px", paddingLeft: "10px" }}>이름</td>
            <td style={{ padding: "14px", paddingLeft: "10px" }}>학년</td>
            <td style={{ padding: "14px", paddingLeft: "10px" }}>학원 반</td>
            <td style={{ padding: "14px", paddingLeft: "10px" }}>
              전화번호 (학생)
            </td>
            <td style={{ padding: "14px", paddingLeft: "10px" }}>
              전화번호 (학부모)
            </td>
            <td style={{ padding: "14px", textAlign: "center" }}>학습 페이지</td>
            <td></td>
          </tr>
          {currentStudents.map((student, index) => {
            const isChecked = selectedStudents.includes(student);
            return (
              <tr key={index}>
                <td
                  style={{
                    textAlign: "center",
                  }}
                >
                  <CustomCheckbox
                    checked={isChecked}
                    onChange={(e) =>
                      handleCheckboxChange(e.target.checked, student)
                    }
                  />
                </td>
                <td style={{ padding: "10px" }}>{student.name}</td>
                <td style={{ padding: "10px" }}>{showGrade(student.grade)}</td>
                <td style={{ padding: "10px" }}>
                  {showClass(student.level_list)}
                </td>
                <td style={{ padding: "10px" }}>{student.student_phone}</td>
                <td style={{ padding: "10px" }}>{student.parent_phone}</td>
                <td style={{ padding: "10px", textAlign: "center" }}>
                  {student.session ? (
                    <button
                      onClick={() => window.open(showStudentURL(student.session), '_blank')}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#11346A",
                        color: "#FFF",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: "500",
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#0F2A5A'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#11346A'}
                    >
                      열기
                    </button>
                  ) : (
                    <span style={{ color: "#999", fontSize: "12px" }}>-</span>
                  )}
                </td>
                <td>
                  <StudentMoreButton student={student} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* 페이지네이션 UI */}
      <Pagination
        visiblePages={visiblePages}
        current_page={currentPage}
        handlePageClick={handlePageClick}
      />

      {/* 하단 Overlay: 선택된 학생 수가 1명 이상이면 표시 */}
      {selectedStudents.length > 0 && (
        <div style={overlayStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "70%",
              margin: "0 auto",
            }}
          >
            <strong>학생 {selectedStudents.length}명 선택됨</strong>
            <DeleteStudentButton
              students={selectedStudents}
              setSelectedStudents={setSelectedStudents}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTable;
